import React, { useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import PropTypes from 'prop-types'
import Typography from '../../../../components/common/Typography'
import { Avatar, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material'
import Rating from '@mui/material/Rating'
import { MdRefresh, MdLocationOn, MdAttachMoney, MdVisibility, MdClose } from 'react-icons/md'
import { IoIosArrowBack, IoIosArrowForward } from 'react-icons/io'
import { formatDateDashed, formatTime } from '../../../../shared/utils/dateUtils'
import Button from '../../../../components/common/Button'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import {
  fetchAgentTeams,
  fetchAgentTeamMembers,
  fetchAgentContacts,
  fetchAgentReviews,
} from '../../../../state/redux/actions/agentActions'

const AgentActivityTabs = ({ handleRefresh: externalRefresh, agent }) => {
  const dispatch = useDispatch()
  const { agentId } = useParams()
  const hasFetchedRef = React.useRef(false)

  // Redux state
  const {
    teams,
    teamsLoading,
    teamsError,
    teamMembers,
    teamMembersLoading,
    teamMembersError,
    contacts,
    contactsLoading,
    contactsError,
    reviews,
    reviewsLoading,
    reviewsError,
  } = useSelector(state => state.agent)

  const { agentProperties, loading: propertiesLoading, error: propertiesError } = useSelector(state => state.property)

  // Local state
  const [activeTab, setActiveTab] = useState('review_rating')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [selectedProperty, setSelectedProperty] = useState(null)
  const [isModalOpen, setIsModalOpen] = useState(false)

  // Fetch all data when component mounts with the agentId
  useEffect(() => {
    if (agentId && !hasFetchedRef.current) {
      console.log('Fetching agent data for:', agentId)
      hasFetchedRef.current = true
      dispatch(fetchAgentTeams(agentId))
      dispatch(fetchAgentTeamMembers(agentId))
      dispatch(fetchAgentContacts(agentId))
      dispatch(fetchAgentReviews(agentId))
    }
  }, [agentId, dispatch])

  // Reset to page 1 when switching tabs
  useEffect(() => {
    setCurrentPage(1)
  }, [activeTab])

  const handleRefresh = () => {
    if (agentId) {
      console.log('Manual refresh triggered for agent:', agentId)
      dispatch(fetchAgentTeams(agentId))
      dispatch(fetchAgentTeamMembers(agentId))
      dispatch(fetchAgentContacts(agentId))
      dispatch(fetchAgentReviews(agentId))
      if (externalRefresh) externalRefresh()
    }
  }

  // Determine tab counts dynamically
  const tabs = [
    { id: 'review_rating', label: 'Review & Rating', count: reviews?.length || 0 },
    { id: 'propertyListing', label: 'Property Listings', count: agentProperties?.length || 0 },
    { id: 'teamCreated', label: 'Team Created', count: teams?.length || 0 },
    { id: 'contactCreated', label: 'Contact Created', count: contacts?.length || 0 },
    { id: 'teamMembers', label: 'Team Members', count: teamMembers?.length || 0 },
  ]

  // Pagination logic for properties
  const startIndex = (currentPage - 1) * itemsPerPage
  const endIndex = startIndex + itemsPerPage
  const currentProperties = agentProperties?.slice(startIndex, endIndex) || []
  const totalPages = Math.ceil((agentProperties?.length || 0) / itemsPerPage)

  // Pagination for teams
  const currentTeams = teams?.slice(startIndex, endIndex) || []
  const teamsTotalPages = Math.ceil((teams?.length || 0) / itemsPerPage)

  // Pagination for contacts
  const currentContacts = contacts?.slice(startIndex, endIndex) || []
  const contactsTotalPages = Math.ceil((contacts?.length || 0) / itemsPerPage)

  // Pagination for team members
  const currentTeamMembers = teamMembers?.slice(startIndex, endIndex) || []
  const teamMembersTotalPages = Math.ceil((teamMembers?.length || 0) / itemsPerPage)

  const formatPrice = price => {
    if (!price) return 'Price not set'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
    }).format(price)
  }

  const getStatusColor = status => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'available':
        return 'text-teal-600 bg-teal-100'
      case 'sold':
        return 'text-red-600 bg-red-100'
      case 'pending':
        return 'text-yellow-600 bg-yellow-100'
      case 'draft':
        return 'text-gray-600 bg-gray-100'
      default:
        return 'text-teal-600 bg-teal-100'
    }
  }

  const handleViewProperty = property => {
    setSelectedProperty(property)
    setIsModalOpen(true)
  }

  const handleCloseModal = () => {
    setIsModalOpen(false)
    setSelectedProperty(null)
  }


  return (
    <>
      <div className="py-4">
        <div className="bg-white">
          {/* Tabs Navigation */}
          <div className="border-b border-white">
            <div className="flex pl-40">
              {tabs.map(tab => {
                return (
                  <button
                    key={tab.id}
                    className={`px-6  py-3 text-xs font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-gray-900 text-gray-900'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label} ({tab.count})
                  </button>
                )
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'review_rating' && (
              <>
                {reviewsLoading ? (
                  <div className="text-center py-8">
                    <LoadingIndicator />
                  </div>
                ) : reviewsError ? (
                  <div className="py-8">
                    <ErrorState message={reviewsError} />
                  </div>
                ) : reviews?.length === 0 ? (
                  <div className="text-center py-8">
                    <Typography className="text-gray-500">No reviews found for this agent.</Typography>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {reviews.map(review => (
                      <div key={review._id} className="flex items-start gap-4 py-4">
                        {/* Avatar */}
                        <Avatar src={review.reviewerAvatar || '/assets/images/avatar.jpg'} alt={review.reviewerName} className="w-12 h-12" />

                        {/* Content */}
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-1">
                            <Typography className="font-semibold text-sm text-gray-900">{review.reviewerName}</Typography>
                            <Typography className="text-xs text-gray-800">{formatDateDashed(review.createdAt)}</Typography>
                          </div>
                          <Typography className="text-gray-500 text-xs">{review.comment}</Typography>
                        </div>

                        {/* Rating */}
                        <div className="flex items-center gap-2">
                          <Typography className="text-sm font-semibold text-gray-900">{review.rating}</Typography>
                          <Rating
                            name={`rating-${review._id}`}
                            value={review.rating}
                            precision={0.5}
                            readOnly
                            size="small"
                            sx={{
                              '& .MuiRating-iconFilled': {
                                color: '#FFA500',
                              },
                              '& .MuiRating-iconEmpty': {
                                color: '#E0E0E0',
                              },
                            }}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'propertyListing' && (
              <>
                {/* Header with pagination controls */}
                <div className="my-4">
                  <div className="flex items-center justify-between">
                    <Typography variant="h4" className="font-semibold text-teal-700">
                      Properties ({agentProperties?.length || 0})
                    </Typography>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Typography className="text-secondary text-sm">
                          {`${startIndex + 1}-${Math.min(endIndex, agentProperties?.length || 0)} of ${
                            agentProperties?.length || 0
                          }`}
                        </Typography>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 border rounded-full border-teal-500 cursor-pointer hover:bg-teal-500 hover:text-white transition-colors text-teal-600"
                          onClick={handleRefresh}
                          title="Refresh properties"
                        >
                          <MdRefresh />
                        </button>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          title="Previous page"
                        >
                          <IoIosArrowBack className="text-teal-600" />
                        </button>

                        <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded text-sm font-medium">
                          {currentPage} of {totalPages || 1}
                        </span>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                          disabled={currentPage >= totalPages}
                          title="Next page"
                        >
                          <IoIosArrowForward className="text-teal-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Properties Table/List */}
                {propertiesLoading ? (
                  <div className="text-center py-8">
                    <LoadingIndicator />
                  </div>
                ) : propertiesError ? (
                  <div className="py-8">
                    <ErrorState message={propertiesError} />
                  </div>
                ) : agentProperties?.length === 0 ? (
                  <div className="text-center py-8">
                    <Typography className="text-gray-500">No properties found for this agent.</Typography>
                  </div>
                ) : (
                  <div className="space-y-4 overflow-x-auto">
                    {/* Table Header */}
                    <div className="grid grid-cols-8 gap-6 py-3 px-6 min-w-[1200px]">
                      <div className="min-w-[120px]">
                        <Typography variant="h5" className="font-semibold">
                          Property ID
                        </Typography>
                      </div>
                      <div className="min-w-[180px]">
                        <Typography variant="h5" className="font-semibold">
                          Title
                        </Typography>
                      </div>
                      <div className="min-w-[100px]">
                        <Typography variant="h5" className="font-semibold">
                          Status
                        </Typography>
                      </div>
                      <div className="min-w-[120px]">
                        <Typography variant="h5" className="font-semibold">
                          Price
                        </Typography>
                      </div>
                      <div className="min-w-[140px]">
                        <Typography variant="h5" className="font-semibold">
                          Type
                        </Typography>
                      </div>
                      <div className="min-w-[160px]">
                        <Typography variant="h5" className="font-semibold">
                          Location
                        </Typography>
                      </div>
                      <div className="min-w-[130px]">
                        <Typography variant="h5" className="font-semibold">
                          Date Posted
                        </Typography>
                      </div>
                      <div className="min-w-[100px]">
                        <Typography variant="h5" className="font-semibold">
                          Actions
                        </Typography>
                      </div>
                    </div>

                    {/* Property Rows */}
                    {currentProperties.map((property, index) => (
                      <div
                        key={property._id}
                        className={`grid grid-cols-8 gap-6 py-4 px-6 hover:shadow-md hover:border-teal-300 transition-all min-w-[1200px] ${
                          index % 2 === 0 ? 'bg-white' : 'bg-teal-25'
                        }`}
                      >
                        <div className="flex flex-col min-w-[120px]">
                          <Typography className="font-mono text-sm font-medium break-all">{property._id}</Typography>
                        </div>

                        <div className="flex flex-col min-w-[180px]">
                          <Typography className="font-medium text-sm leading-tight">
                            {property.property_title || property.title || 'Untitled'}
                          </Typography>
                          {property.bedrooms && property.bathrooms && (
                            <Typography className="text-xs text-gray-500 mt-1">
                              {property.bedrooms} bed, {property.bathrooms} bath
                            </Typography>
                          )}
                        </div>

                        <div className="min-w-[100px]">
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${getStatusColor(
                              property.availability || property.status
                            )}`}
                          >
                            {property.availability || property.status || 'Unknown'}
                          </span>
                        </div>

                        <div className="flex items-center gap-1 min-w-[120px]">
                          <MdAttachMoney className="text-teal-600 text-sm flex-shrink-0" />
                          <Typography className="text-sm font-medium">{formatPrice(property.price)}</Typography>
                        </div>

                        <div className="min-w-[140px]">
                          <Typography className="text-sm leading-tight">
                            {property.listing_type || property.property_type || 'N/A'}
                          </Typography>
                          {property.structure && (
                            <Typography className="text-xs text-gray-500 mt-1">
                              {property.structure.replace(/_/g, ' ')}
                            </Typography>
                          )}
                        </div>

                        <div className="flex items-start gap-1 min-w-[160px]">
                          <MdLocationOn className="text-teal-500 text-sm flex-shrink-0 mt-0.5" />
                          <Typography className="text-sm leading-tight">
                            {property.location || property.address || 'Location not set'}
                          </Typography>
                        </div>

                        <div className="min-w-[130px]">
                          <Typography className="text-sm leading-tight">
                            {formatDateDashed(property.date_posted || property.createdAt)}
                          </Typography>
                          <Typography className="text-xs text-gray-500 mt-1">
                            {formatTime(property.date_posted || property.createdAt)}
                          </Typography>
                        </div>

                        <div className="flex items-center gap-2 min-w-[100px]">
                          <Button
                            variant="outline"
                            size="small"
                            onClick={() => handleViewProperty(property)}
                            className="text-xs px-2 py-1 whitespace-nowrap border-teal-500 text-teal-600 hover:bg-teal-50"
                          >
                            <MdVisibility className="mr-1" />
                            View
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {activeTab === 'teamCreated' && (
              <>
                {/* Header with pagination controls */}
                <div className="my-4">
                  <div className="flex items-center justify-between">
                    <Typography variant="h4" className="font-semibold text-teal-700">
                      Teams Created ({teams?.length || 0})
                    </Typography>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Typography className="text-secondary text-sm">
                          {`${startIndex + 1}-${Math.min(endIndex, teams?.length || 0)} of ${teams?.length || 0}`}
                        </Typography>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 border rounded-full border-teal-500 cursor-pointer hover:bg-teal-500 hover:text-white transition-colors text-teal-600"
                          onClick={handleRefresh}
                          title="Refresh teams"
                        >
                          <MdRefresh />
                        </button>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          title="Previous page"
                        >
                          <IoIosArrowBack className="text-teal-600" />
                        </button>

                        <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded text-sm font-medium">
                          {currentPage} of {teamsTotalPages || 1}
                        </span>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.min(teamsTotalPages, p + 1))}
                          disabled={currentPage >= teamsTotalPages}
                          title="Next page"
                        >
                          <IoIosArrowForward className="text-teal-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Teams Table */}
                {teamsLoading ? (
                  <div className="text-center py-8">
                    <LoadingIndicator />
                  </div>
                ) : teamsError ? (
                  <div className="py-8">
                    <ErrorState message={teamsError} />
                  </div>
                ) : teams?.length === 0 ? (
                  <div className="text-center py-8">
                    <Typography className="text-gray-500">No teams created yet.</Typography>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-5 gap-4 py-3 px-4 bg-gray-50 rounded-t-md">
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Team Name
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Description
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Members
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Created At
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Updated At
                      </Typography>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {currentTeams.map((team, index) => (
                        <div
                          key={team._id || index}
                          className={`grid grid-cols-5 gap-4 py-4 px-4 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-teal-25'
                          }`}
                        >
                          <Typography className="text-sm">{team.name || 'N/A'}</Typography>
                          <Typography className="text-sm">{team.description || 'No description'}</Typography>
                          <Typography className="text-sm">{team.members?.length || 0}</Typography>
                          <Typography className="text-sm">{formatDateDashed(team.createdAt)}</Typography>
                          <Typography className="text-sm">{formatDateDashed(team.updatedAt)}</Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'contactCreated' && (
              <>
                {/* Header with pagination controls */}
                <div className="my-4">
                  <div className="flex items-center justify-between">
                    <Typography variant="h4" className="font-semibold text-teal-700">
                      Contacts Created ({contacts?.length || 0})
                    </Typography>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Typography className="text-secondary text-sm">
                          {`${startIndex + 1}-${Math.min(endIndex, contacts?.length || 0)} of ${contacts?.length || 0}`}
                        </Typography>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 border rounded-full border-teal-500 cursor-pointer hover:bg-teal-500 hover:text-white transition-colors text-teal-600"
                          onClick={handleRefresh}
                          title="Refresh contacts"
                        >
                          <MdRefresh />
                        </button>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          title="Previous page"
                        >
                          <IoIosArrowBack className="text-teal-600" />
                        </button>

                        <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded text-sm font-medium">
                          {currentPage} of {contactsTotalPages || 1}
                        </span>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.min(contactsTotalPages, p + 1))}
                          disabled={currentPage >= contactsTotalPages}
                          title="Next page"
                        >
                          <IoIosArrowForward className="text-teal-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Contacts Table */}
                {contactsLoading ? (
                  <div className="text-center py-8">
                    <LoadingIndicator />
                  </div>
                ) : contactsError ? (
                  <div className="py-8">
                    <ErrorState message={contactsError} />
                  </div>
                ) : contacts?.length === 0 ? (
                  <div className="text-center py-8">
                    <Typography className="text-gray-500">No contacts created yet.</Typography>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-5 gap-4 py-3 px-4 bg-gray-50 rounded-t-md">
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Name
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Email
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Phone
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Company
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Date Added
                      </Typography>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {currentContacts.map((contact, index) => (
                        <div
                          key={contact._id || index}
                          className={`grid grid-cols-5 gap-4 py-4 px-4 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-teal-25'
                          }`}
                        >
                          <Typography className="text-sm">{contact.name || 'N/A'}</Typography>
                          <Typography className="text-sm">{contact.email || 'N/A'}</Typography>
                          <Typography className="text-sm">{contact.phone || 'N/A'}</Typography>
                          <Typography className="text-sm">{contact.company || 'N/A'}</Typography>
                          <Typography className="text-sm">{formatDateDashed(contact.createdAt)}</Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}

            {activeTab === 'teamMembers' && (
              <>
                {/* Header with pagination controls */}
                <div className="my-4">
                  <div className="flex items-center justify-between">
                    <Typography variant="h4" className="font-semibold text-teal-700">
                      Team Members ({teamMembers?.length || 0})
                    </Typography>

                    <div className="flex items-center gap-4">
                      <div className="flex items-center gap-2">
                        <Typography className="text-secondary text-sm">
                          {`${startIndex + 1}-${Math.min(endIndex, teamMembers?.length || 0)} of ${
                            teamMembers?.length || 0
                          }`}
                        </Typography>
                      </div>

                      <div className="flex items-center gap-2">
                        <button
                          className="p-2 border rounded-full border-teal-500 cursor-pointer hover:bg-teal-500 hover:text-white transition-colors text-teal-600"
                          onClick={handleRefresh}
                          title="Refresh team members"
                        >
                          <MdRefresh />
                        </button>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                          disabled={currentPage === 1}
                          title="Previous page"
                        >
                          <IoIosArrowBack className="text-teal-600" />
                        </button>

                        <span className="px-3 py-1 bg-teal-100 text-teal-800 rounded text-sm font-medium">
                          {currentPage} of {teamMembersTotalPages || 1}
                        </span>

                        <button
                          className="p-2 border border-teal-300 rounded-full disabled:opacity-30 disabled:cursor-not-allowed hover:bg-teal-50"
                          onClick={() => setCurrentPage(p => Math.min(teamMembersTotalPages, p + 1))}
                          disabled={currentPage >= teamMembersTotalPages}
                          title="Next page"
                        >
                          <IoIosArrowForward className="text-teal-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Team Members Table */}
                {teamMembersLoading ? (
                  <div className="text-center py-8">
                    <LoadingIndicator />
                  </div>
                ) : teamMembersError ? (
                  <div className="py-8">
                    <ErrorState message={teamMembersError} />
                  </div>
                ) : teamMembers?.length === 0 ? (
                  <div className="text-center py-8">
                    <Typography className="text-gray-500">No team members found.</Typography>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-5 gap-4 py-3 px-4 bg-gray-50 rounded-t-md">
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Name
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Email
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Role
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Team
                      </Typography>
                      <Typography variant="h5" className="font-semibold text-teal-700">
                        Joined At
                      </Typography>
                    </div>
                    <div className="divide-y divide-gray-200">
                      {currentTeamMembers.map((member, index) => (
                        <div
                          key={member._id || index}
                          className={`grid grid-cols-5 gap-4 py-4 px-4 ${
                            index % 2 === 0 ? 'bg-white' : 'bg-teal-25'
                          }`}
                        >
                          <Typography className="text-sm">{member.name || member.full_name || 'N/A'}</Typography>
                          <Typography className="text-sm">{member.email || 'N/A'}</Typography>
                          <Typography className="text-sm">{member.role || 'Member'}</Typography>
                          <Typography className="text-sm">{member.teamName || 'N/A'}</Typography>
                          <Typography className="text-sm">{formatDateDashed(member.createdAt || member.joinedAt)}</Typography>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Property Details Modal */}
      <Dialog
        open={isModalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        slotProps={{
          paper: {
            className: 'rounded-lg',
          },
        }}
      >
        <DialogTitle className="flex justify-between items-center bg-teal-50 text-teal-700">
          <Typography variant="h4" className="font-semibold">
            Property Details
          </Typography>
          <button onClick={handleCloseModal} className="p-1 hover:bg-teal-100 rounded-full">
            <MdClose className="text-xl text-teal-600" />
          </button>
        </DialogTitle>

        <DialogContent className="p-6">
          {selectedProperty && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <Typography variant="h6" className="font-semibold text-teal-700 mb-2">
                    Basic Information
                  </Typography>
                  <div className="space-y-2">
                    <div>
                      <Typography className="font-medium text-gray-700">Property ID:</Typography>
                      <Typography className="font-mono text-sm text-teal-600">{selectedProperty._id}</Typography>
                    </div>
                    <div>
                      <Typography className="font-medium text-gray-700">Title:</Typography>
                      <Typography>
                        {selectedProperty.property_title || selectedProperty.title || 'Untitled'}
                      </Typography>
                    </div>
                    <div>
                      <Typography className="font-medium text-gray-700">Status:</Typography>
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(
                          selectedProperty.availability || selectedProperty.status
                        )}`}
                      >
                        {selectedProperty.availability || selectedProperty.status || 'Unknown'}
                      </span>
                    </div>
                    <div>
                      <Typography className="font-medium text-gray-700">Type:</Typography>
                      <Typography>
                        {selectedProperty.listing_type || selectedProperty.property_type || 'N/A'}
                      </Typography>
                    </div>
                  </div>
                </div>

                <div>
                  <Typography variant="h6" className="font-semibold text-teal-700 mb-2">
                    Details
                  </Typography>
                  <div className="space-y-2">
                    <div>
                      <Typography className="font-medium text-gray-700">Price:</Typography>
                      <Typography className="text-teal-600 font-semibold">
                        {formatPrice(selectedProperty.price)}
                      </Typography>
                    </div>
                    <div>
                      <Typography className="font-medium text-gray-700">Location:</Typography>
                      <Typography>
                        {selectedProperty.location || selectedProperty.address || 'Location not set'}
                      </Typography>
                    </div>
                    {selectedProperty.bedrooms && (
                      <div>
                        <Typography className="font-medium text-gray-700">Bedrooms:</Typography>
                        <Typography>{selectedProperty.bedrooms}</Typography>
                      </div>
                    )}
                    {selectedProperty.bathrooms && (
                      <div>
                        <Typography className="font-medium text-gray-700">Bathrooms:</Typography>
                        <Typography>{selectedProperty.bathrooms}</Typography>
                      </div>
                    )}
                    {selectedProperty.structure && (
                      <div>
                        <Typography className="font-medium text-gray-700">Structure:</Typography>
                        <Typography>{selectedProperty.structure.replace(/_/g, ' ')}</Typography>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <Typography variant="h6" className="font-semibold text-teal-700 mb-2">
                  Timeline
                </Typography>
                <div className="space-y-2">
                  <div>
                    <Typography className="font-medium text-gray-700">Date Posted:</Typography>
                    <Typography>{formatDateDashed(selectedProperty.date_posted || selectedProperty.createdAt)}</Typography>
                  </div>
                  <div>
                    <Typography className="font-medium text-gray-700">Time Posted:</Typography>
                    <Typography>{formatTime(selectedProperty.date_posted || selectedProperty.createdAt)}</Typography>
                  </div>
                </div>
              </div>

              {selectedProperty.description && (
                <div>
                  <Typography variant="h6" className="font-semibold text-teal-700 mb-2">
                    Description
                  </Typography>
                  <Typography className="text-gray-700">{selectedProperty.description}</Typography>
                </div>
              )}
            </div>
          )}
        </DialogContent>

        <DialogActions className="px-6 pb-6">
          <Button
            variant="outline"
            onClick={handleCloseModal}
            className="border-teal-500 text-teal-600 hover:bg-teal-50"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default AgentActivityTabs

AgentActivityTabs.propTypes = {
  handleRefresh: PropTypes.func,
  agent: PropTypes.object.isRequired,
}
